<template>
  <div class="multicast-list">
    <!-- 科技感装饰元素 -->
    <div class="tech-corner top-left"></div>
    <div class="tech-corner top-right"></div>
    <div class="tech-corner bottom-left"></div>
    <div class="tech-corner bottom-right"></div>

    <div class="list-header">
      <div class="header-title">
        <svg-icon name="list" class="header-icon"></svg-icon>
        {{$translate('multicastConfigManagement')}}
      </div>
      <div class="header-actions">
        <Button type="primary" @click="$emit('add')" class="add-btn">
          <svg-icon name="plus" class="btn-icon"></svg-icon>
          {{$translate('addConfiguration')}}
        </Button>
        <Button type="text" @click="$emit('refresh')" class="refresh-btn">
          <svg-icon name="refresh" class="refresh-icon"></svg-icon>
          {{$translate('refresh')}}
        </Button>
      </div>
    </div>

    <div class="list-content">
      <div v-if="loading" class="loading-container">
        <Spin size="large">
          <div class="loading-text">{{$translate('loading')}}</div>
        </Spin>
      </div>

      <div v-else-if="dataList.length === 0" class="empty-container">
        <div class="empty-icon">
          <svg-icon name="empty" class="empty-svg"></svg-icon>
        </div>
        <div class="empty-text">{{$translate('noMulticastConfig')}}</div>
        <div class="empty-desc">{{$translate('clickAddConfigTip')}}</div>
      </div>

      <div v-else class="data-list">
        <div
          v-for="item in dataList"
          :key="item.id"
          class="list-item"
          :class="{ 'disabled': item.enabled === 2 }"
        >
          <div class="item-content">
            <div class="item-header">
              <div class="item-title">
                <div class="config-name">{{ item.name }}</div>
                <div class="status-badge" :class="item.enabled === 1 ? 'enabled' : 'disabled'">
                  <span class="status-icon">●</span>
                  {{ item.enabled === 1 ? $translate('enabled') : $translate('disabled') }}
                </div>
                <div class="status-switch">
                  <i-switch
                      :value="item.enabled === 1"
                      @on-change="(value) => handleStatusChange(item, value)"
                      size="large"
                      class="tech-switch"
                  />
                </div>
              </div>
              <div class="item-actions">
                <Button type="primary" @click="$emit('edit', item)" class="edit-btn">
                  <svg-icon name="edit" class="action-icon"></svg-icon>
                  {{$translate('edit')}}
                </Button>
                <Button type="error" @click="$emit('delete', item)" class="delete-btn">
                  <svg-icon name="delete" class="action-icon"></svg-icon>
                  {{$translate('delete')}}
                </Button>
              </div>
            </div>

            <div class="item-details">
              <div class="detail-row">
                <div class="detail-item">
                  <span class="detail-label">{{$translate('ipAddress')}}:</span>
                  <span class="detail-value">{{ item.multicastIp || '-' }}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">{{$translate('telecontrolPort')}}:</span>
                  <span class="detail-value">{{ item.telecontrolPort || '-' }}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">{{$translate('telemetryPort')}}:</span>
                  <span class="detail-value">{{ item.telemetryPort || '-' }}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">{{$translate('videoPort')}}:</span>
                  <span class="detail-value">{{ item.videoPort || '-' }}</span>
                </div>
              </div>

              <div class="robot-info" v-if="item.robots && item.robots.length > 0">
                <span class="robot-label">{{$translate('associatedRobots')}}:</span>
                <div class="robot-tags">
                  <Tag v-for="robot in item.robots.slice(0, 3)" :key="robot.id" class="robot-tag">
                    <span class="robot-name">{{ robot.name }}</span>
                    <span class="robot-code">({{ robot.code }})</span>
                  </Tag>
                  <Tag v-if="item.robots.length > 3" class="more-tag">
                    +{{ item.robots.length - 3 }}
                  </Tag>
                </div>
              </div>
            </div>
          </div>

          <div class="item-decoration">
            <div class="tech-line"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- 分页 -->
    <div class="pagination-container" v-if="!loading && dataList.length > 0">
      <Page
        :current="currentPage"
        :total="total"
        :page-size="15"
        @on-change="$emit('page-change', $event)"
        show-total
        class="tech-pagination"
      />
    </div>
  </div>
</template>

<script>
export default {
  name: 'MulticastList',
  props: {
    loading: {
      type: Boolean,
      default: false
    },
    dataList: {
      type: Array,
      default: () => []
    },
    total: {
      type: Number,
      default: 0
    },
    currentPage: {
      type: Number,
      default: 1
    }
  },
  methods: {
    // 处理状态切换
    handleStatusChange(item, enabled) {
      this.$emit('status-change', item, enabled)
    }
  }
}
</script>

<style lang="less" scoped>
.multicast-list {
  position: relative;
  width: 100%;
  height: 100%;
  background: rgba(10, 25, 50, 0.8);
  border-radius: 15px;
  border: 1px solid rgba(0, 191, 255, 0.3);
  backdrop-filter: blur(10px);
  box-shadow:
    0 0 20px rgba(0, 191, 255, 0.1),
    inset 0 0 10px rgba(0, 191, 255, 0.05);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* 科技感边角 */
.tech-corner {
  position: absolute;
  width: 20px;
  height: 20px;
  border: 1px solid rgba(0, 191, 255, 0.6);
  z-index: 5;
}

.top-left {
  top: 0;
  left: 0;
  border-right: none;
  border-bottom: none;
  border-top-left-radius: 5px;
}

.top-right {
  top: 0;
  right: 0;
  border-left: none;
  border-bottom: none;
  border-top-right-radius: 5px;
}

.bottom-left {
  bottom: 0;
  left: 0;
  border-right: none;
  border-top: none;
  border-bottom-left-radius: 5px;
}

.bottom-right {
  bottom: 0;
  right: 0;
  border-left: none;
  border-top: none;
  border-bottom-right-radius: 5px;
}

.list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 5px 10px;
  border-bottom: 1px solid rgba(0, 191, 255, 0.2);
  background: rgba(15, 35, 70, 0.6);
}

.header-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 18px;
  font-weight: 600;
  color: #ffffff;
}

.header-icon {
  font-size: 20px;
  color: #00bfff;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 15px;
}

.add-btn {
  background: linear-gradient(135deg, #00bfff, #0080ff);
  border: none;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 14px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 4px 15px rgba(0, 191, 255, 0.3);
  transition: all 0.3s ease;
}

.add-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 191, 255, 0.4);
}

.refresh-btn {
  color: #00bfff;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 10px;
  font-size: 14px;
  border-radius: 6px;
  transition: all 0.3s ease;
  background: rgba(0, 191, 255, 0.1);
  border: 1px solid rgba(0, 191, 255, 0.3);
}

.refresh-btn:hover {
  color: #ffffff;
  background: rgba(0, 191, 255, 0.2);
  border-color: rgba(0, 191, 255, 0.5);
}

.btn-icon,
.refresh-icon {
  font-size: 16px;
}

.list-content {
  flex: 1;
  overflow-y: auto;
  padding: 10px;
}

.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
}

.loading-text {
  margin-top: 10px;
  color: #00bfff;
  font-size: 14px;
}

.empty-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 300px;
  color: rgba(255, 255, 255, 0.6);
}

.empty-icon {
  margin-bottom: 20px;
}

.empty-svg {
  font-size: 64px;
  color: rgba(0, 191, 255, 0.3);
}

.empty-text {
  font-size: 18px;
  margin-bottom: 10px;
}

.empty-desc {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.4);
}

.data-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.list-item {
  position: relative;
  background: rgba(15, 35, 70, 0.6);
  border: 1px solid rgba(0, 191, 255, 0.2);
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.3s ease;
}

.list-item:hover {
  border-color: rgba(0, 191, 255, 0.4);
  box-shadow: 0 0 20px rgba(0, 191, 255, 0.1);
  transform: translateY(-2px);
}

.list-item.disabled {
  opacity: 0.6;
}

.item-content {
  padding: 10px;
}

.item-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 3px;
}

.item-title {
  display: flex;
  align-items: center;
  gap: 15px;
}

.config-name {
  font-size: 18px;
  font-weight: 600;
  color: #ff8447;
}

.status-badge {
  padding: 3px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 4px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.status-badge.enabled {
  background: linear-gradient(135deg, #00ff88, #00cc6a);
  color: #ffffff;
  border: 1px solid #00ff88;
  box-shadow:
    0 0 8px rgba(0, 255, 136, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);
  text-shadow: 0 1px 1px rgba(0, 0, 0, 0.3);
}

.status-badge.disabled {
  background: linear-gradient(135deg, #ff4757, #ff3742);
  color: #ffffff;
  border: 1px solid #ff4757;
  box-shadow:
    0 0 8px rgba(255, 71, 87, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);
  text-shadow: 0 1px 1px rgba(0, 0, 0, 0.3);
}

.status-icon {
  font-size: 8px;
  animation: pulse 2s infinite;
}

.status-badge.enabled .status-icon {
  color: #ffffff;
  text-shadow: 0 0 5px #00ff88;
}

.status-badge.disabled .status-icon {
  color: #ffffff;
  text-shadow: 0 0 5px #ff4757;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.7;
    transform: scale(1.1);
  }
}

.item-actions {
  display: flex;
  align-items: center;
  gap: 15px;
}

.status-switch {
  display: flex;
  align-items: center;
  gap: 8px;
}

.switch-label {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
}

.tech-switch {
  background: rgba(0, 191, 255, 0.2);
}

.edit-btn {
  background: linear-gradient(135deg, #00bfff, #0080ff);
  border: none;
  border-radius: 6px;
  display: flex;
  align-items: center;
  font-size: 14px;
  height: 28px;
  line-height: 28px;
}

.delete-btn {
  background: linear-gradient(135deg, #ffbfc5, #ed000d);
  border: none;
  border-radius: 6px;
  display: flex;
  align-items: center;
  font-size: 14px;
  height: 28px;
  line-height: 28px;
}

.action-icon {
  font-size: 14px;
}

.item-details {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.detail-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
}

.detail-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.detail-label {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
  min-width: 80px;
}

.detail-value {
  font-size: 14px;
  color: #00bfff;
  font-weight: 500;
}

.robot-info {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.robot-label {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
  min-width: 80px;
}

.robot-tags {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.robot-tag {
  background: linear-gradient(135deg, rgba(0, 191, 255, 0.15), rgba(0, 191, 255, 0.08));
  color: #ffffff;
  border: 1px solid rgba(0, 191, 255, 0.4);
  border-radius: 6px;
  padding: 4px 10px;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: all 0.3s ease;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}

.robot-tag:hover {
  background: linear-gradient(135deg, rgba(0, 191, 255, 0.25), rgba(0, 191, 255, 0.15));
  border-color: rgba(0, 191, 255, 0.6);
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(0, 191, 255, 0.2);
}

.robot-name {
  font-weight: 600;
  color: #ffffff;
}

.robot-code {
  font-weight: 400;
  color: #ffca00;
  font-size: 11px;
}

.more-tag {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.15), rgba(255, 255, 255, 0.08));
  color: #ffffff;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 6px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 600;
  transition: all 0.3s ease;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}

.more-tag:hover {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.25), rgba(255, 255, 255, 0.15));
  border-color: rgba(255, 255, 255, 0.5);
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(255, 255, 255, 0.1);
}

.item-decoration {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 2px;
  overflow: hidden;
}

.tech-line {
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, #00bfff, transparent);
  animation: tech-flow 3s infinite;
}

.pagination-container {
  padding: 12px 20px;
  border-top: 1px solid rgba(0, 191, 255, 0.2);
  background: rgba(15, 35, 70, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
}

/* 动画定义 */
@keyframes tech-flow {
  0% {
    transform: translateX(-100%);
    opacity: 0;
  }
  50% {
    opacity: 1;
  }
  100% {
    transform: translateX(100%);
    opacity: 0;
  }
}

/* 滚动条样式 */
.list-content::-webkit-scrollbar {
  width: 6px;
}

.list-content::-webkit-scrollbar-track {
  background: rgba(0, 191, 255, 0.1);
  border-radius: 3px;
}

.list-content::-webkit-scrollbar-thumb {
  background: rgba(0, 191, 255, 0.3);
  border-radius: 3px;
  transition: all 0.3s ease;
}

.list-content::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 191, 255, 0.5);
}

/* 分页样式 */
.tech-pagination {
  /deep/ .ivu-page-item {
    background: rgba(15, 35, 70, 0.9) !important;
    border: 1px solid rgba(0, 191, 255, 0.5) !important;
    color: #ffffff !important;
    transition: all 0.3s ease !important;
    font-weight: 600 !important;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5) !important;
  }

  /deep/ .ivu-page-item a {
    color: #ffffff !important;
    font-weight: 600 !important;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5) !important;
  }

  /deep/ .ivu-page-item:hover {
    border-color: rgba(0, 191, 255, 0.8) !important;
    background: rgba(0, 191, 255, 0.3) !important;
    color: #ffffff !important;
    box-shadow: 0 0 12px rgba(0, 191, 255, 0.3) !important;
  }

  /deep/ .ivu-page-item:hover a {
    color: #ffffff !important;
  }

  /deep/ .ivu-page-item-active {
    background: linear-gradient(135deg, #00bfff, #0080ff) !important;
    border-color: rgba(0, 191, 255, 1) !important;
    color: #ffffff !important;
    box-shadow: 0 0 15px rgba(0, 191, 255, 0.5) !important;
    font-weight: 700 !important;
  }

  /deep/ .ivu-page-item-active a {
    color: #ffffff !important;
    font-weight: 700 !important;
    text-shadow: 0 1px 3px rgba(0, 0, 0, 0.6) !important;
  }

  /deep/ .ivu-page-prev,
  /deep/ .ivu-page-next {
    background: rgba(15, 35, 70, 0.9) !important;
    border: 1px solid rgba(0, 191, 255, 0.5) !important;
    color: #ffffff !important;
    font-weight: 600 !important;
  }

  /deep/ .ivu-page-prev a,
  /deep/ .ivu-page-next a {
    color: #ffffff !important;
    font-weight: 600 !important;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5) !important;
  }

  /deep/ .ivu-page-prev:hover,
  /deep/ .ivu-page-next:hover {
    border-color: rgba(0, 191, 255, 0.8) !important;
    background: rgba(0, 191, 255, 0.3) !important;
    color: #ffffff !important;
    box-shadow: 0 0 12px rgba(0, 191, 255, 0.3) !important;
  }

  /deep/ .ivu-page-prev:hover a,
  /deep/ .ivu-page-next:hover a {
    color: #ffffff !important;
  }

  /deep/ .ivu-page-total {
    color: #ffffff !important;
    font-weight: 600 !important;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5) !important;
  }

  /deep/ .ivu-page-options-elevator input {
    background: rgba(15, 35, 70, 0.9) !important;
    border: 1px solid rgba(0, 191, 255, 0.5) !important;
    color: #ffffff !important;
    font-weight: 600 !important;
  }

  /deep/ .ivu-page-options-elevator input:focus {
    border-color: rgba(0, 191, 255, 0.8) !important;
    box-shadow: 0 0 10px rgba(0, 191, 255, 0.4) !important;
  }
}
// 开关样式增强 - 与表单完全一致
/deep/ .ivu-switch {
  background: rgba(15, 35, 70, 0.6);
  border: 1px solid rgba(0, 191, 255, 0.3);
  transition: all 0.3s ease;
}

/deep/ .ivu-switch:hover {
  border-color: rgba(0, 191, 255, 0.5);
}

/deep/ .ivu-switch-inner {
  background: rgba(255, 255, 255, 0.9);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

/deep/ .tech-switch.ivu-switch-checked {
  background: linear-gradient(135deg, #00bfff, #0080ff);
  box-shadow: 0 0 10px rgba(0, 191, 255, 0.3);
  border-color: rgba(0, 191, 255, 0.8);
}
</style>
